import React, { useContext, useState, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IItemForGetShortInfo,
	IGetComboModifiersShort,
	IGetComboModifiersShortResponse,
	UserRoles,
	ITotalPrice,
} from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { getComboModifiersShortInfo } from 'backend/comboModifiers';
import FormSelect from 'components/Select/FormSelect';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './ComboModifierSelect.module.scss';

interface IComboModifierSelect {
	setComboModifier: (value: Nullable<string>) => void;
	updateComboModifier(value: IItemForGetShortInfo[]): void;
	setFetchedComboModifier: (value: IItemForGetShortInfo[]) => void;
	totalPrices: ITotalPrice[];
	initialSelectedComboModifier: Nullable<string>;
}

const ComboModifierSelect: React.FC<IComboModifierSelect> = ({
	setComboModifier,
	updateComboModifier,
	setFetchedComboModifier,
	totalPrices,
	initialSelectedComboModifier,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);

	// TODO You will need to think about the logic.
	// We do not know whether or not there is access to the restaurant from which ComboModifiers came.
	// I think we need to remove this logic. It seems unnecessary.
	const restaurantIdsOfTotalPrices = totalPrices.map((el) => el.restaurantId);
	const restaurantIdsOfTotalPricesWithLimitedAccess = totalPrices
		.map((el) => el.restaurantId)
		.filter((el) => accessibleRestaurantIds?.includes(el));
	const { response, loading, refetch, responseError } = useFetchData<
		IGetComboModifiersShortResponse,
		IGetComboModifiersShort
	>(
		(hasAccess ? totalPrices.length : restaurantIdsOfTotalPricesWithLimitedAccess.length)
			? getComboModifiersShortInfo
			: null,
		{
			restaurantIds: hasAccess ? restaurantIdsOfTotalPrices : restaurantIdsOfTotalPricesWithLimitedAccess,
		},
	);
	// TODO You will need to think about the logic.
	// We do not know whether or not there is access to the restaurant from which ComboModifiers came.
	// I think we need to remove this logic. It seems unnecessary.

	const updatedData = response?.data.comboModifiers.map((el) => ({
		...el,
		additionalContent: (
			<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
		),
	}));

	useEffect(() => {
		if (totalPrices.length) {
			refetch();
		} else {
			setComboModifier(null);
		}
	}, [totalPrices]);

	useEffect(() => {
		if (isFirstRender && response) {
			setComboModifier(initialSelectedComboModifier);
			setIsFirstRender(false);
		} else if (response) {
			setFetchedComboModifier(response.data.comboModifiers);
			updateComboModifier(response.data.comboModifiers);
		}
	}, [initialSelectedComboModifier, isFirstRender, response]);

	return (
		<FormSelect
			testId="ComboModifierSelect"
			name="selectedComboModifier"
			label="comboModifierLabel"
			options={updatedData}
			fetchError={responseError}
			loading={loading}
			disabled={!totalPrices.length || loading}
			onRefetch={refetch}
			onChange={setComboModifier}
		/>
	);
};

export default React.memo(ComboModifierSelect);
